/**
 * Built-in white-label brand presets, selectable with ONE env var
 * (VITE_BRAND=xfincompass|toponelogic) instead of setting every VITE_BRAND_*.
 *
 * Plain data — NO `import.meta` here — so BOTH the browser bundle
 * (src/lib/brand.ts) and the Node build config (vite.config.ts) can import it.
 * Any individual VITE_BRAND_* env still overrides a single field on top of the
 * chosen preset. Add a brand here to make it switchable; mirror it in the BE's
 * internal/config/brand_presets.go.
 *
 * Repo này là xFinCompass, nên DEFAULT_BRAND = xfincompass. Preset Toponelogic
 * được GIỮ LẠI vì code đồng bộ định kỳ từ upstream `toponelogic_client` — giữ cả
 * hai preset làm diff của lần merge sau chỉ còn đúng hằng số dưới đây.
 *
 * ⚠️ SYNC: file này + brand.ts là 2 chỗ CỐ Ý lệch upstream. Merge từ upstream về
 * thì luôn giữ phía shine_client (`git checkout --ours`) — xem docs/SYNC-UPSTREAM.md.
 */
export type BrandPreset = {
  name: string;
  legalName: string;
  monogram: string;
  slug: string;
  domain: string;
  siteUrl: string;
  supportEmail: string;
  legalEmail: string;
  privacyEmail: string;
  /** Out-of-box skin + accent. Typed as plain strings, NOT `PaletteKey` /
   * `AccentKey`: those live in `entities/user/model/preferences-store`, and
   * `shared/` may not import upward. preferences-store casts on the way in —
   * it is the layer that owns the union and validates against it. */
  defaultPalette: string;
  defaultAccent: string;
  /** Does this brand ship the "connect a brokerage" (SnapTrade) entry point?
   * A PRODUCT decision per white-label build, not an ops toggle — the
   * `portfolio_tab.broker` flag still applies on top for brands that do offer
   * it. The broker code stays compiled in either way; only the way in is
   * withheld, so syncing with the upstream repo stays cheap. */
  offersBrokerConnect: boolean;
};

/** Fallback when VITE_BRAND is unset or unknown — an unconfigured build of this
 * repo is an xFinCompass build. */
export const DEFAULT_BRAND = "xfincompass";

// Each brand is a NAMED export so the browser can select exactly ONE at build
// time (see brand.ts) — Rollup then tree-shakes the other brand's strings out of
// the shipped bundle, so a white-label build never ships a rival brand's name.
// The BRAND_PRESETS map + resolveBrandPreset below exist for the Node build
// config (vite.config.ts), which is NOT shipped to the browser, so it may keep
// looking brands up dynamically by slug.
export const XFINCOMPASS_PRESET: BrandPreset = {
  name: "xFinCompass",
  legalName: "xFinCompass Inc.",
  monogram: "xFC",
  slug: "xfincompass",
  domain: "xfincompass.com",
  siteUrl: "https://xfincompass.org",
  supportEmail: "support@xfincompass.org",
  legalEmail: "legal@xfincompass.org",
  privacyEmail: "privacy@xfincompass.org",
  // Deep navy (#0a0e16) + champagne gold — sếp chốt, xem DESIGN-shineai.md / ADR-021.
  defaultPalette: "shinefin",
  defaultAccent: "gold",
  offersBrokerConnect: false,
};

export const TOPONELOGIC_PRESET: BrandPreset = {
  name: "Toponelogic",
  legalName: "Toponelogic Ltd.",
  monogram: "TOL",
  slug: "toponelogic",
  domain: "toponelogic.com",
  siteUrl: "https://toponelogic.com",
  supportEmail: "support@toponelogic.com",
  legalEmail: "legal@toponelogic.com",
  privacyEmail: "privacy@toponelogic.com",
  // Near-black (#0b0e11) + Binance yellow — rebrand 2026-08.
  defaultPalette: "binance",
  defaultAccent: "binance",
  offersBrokerConnect: true,
};

export const BRAND_PRESETS: Record<string, BrandPreset> = {
  xfincompass: XFINCOMPASS_PRESET,
  toponelogic: TOPONELOGIC_PRESET,
};

/** Resolve a VITE_BRAND slug (case/space-insensitive) to a preset, falling back
 * to Toponelogic for an empty or unknown value. */
export function resolveBrandPreset(slug?: string): BrandPreset {
  const key = (slug || "").trim().toLowerCase();
  return BRAND_PRESETS[key] || BRAND_PRESETS[DEFAULT_BRAND];
}
